import { useAuthContext } from "@/components/core/Auth/AuthProvider"
import { useRouter } from "next/router"
import { useCallback, useEffect, useRef, useState } from "react"

/**
 * Hook to manage feedback modal display logic based on user behavior
 *
 * Conditions for showing modal:
 * - Only shows on homepage (root domain "/")
 * - Logged users: After 3 different episodes completed, then max 1x per 30 days
 * - Anonymous users: After 5 episodes completed OR 10 minutes of usage time
 * - 5-star rating without comment delays next show by 90 days
 *
 * Usage:
 * - Call trackEpisodePlayed(episodeId) when an episode is completed
 * - Modal will automatically show when conditions are met
 * - All data stored in localStorage only
 */

interface FeedbackData {
  lastFeedbackDate?: string
  episodesPlayed: string[] // Array of episode IDs for logged users, or count for anonymous
  anonymousEpisodesCount: number
  usageTimeMinutes: number
  delayUntil?: string // Date when feedback can be shown again
  sessionStartTime?: number
}

const STORAGE_KEY = "feedbackModalData"
const LOGGED_USER_CONDITIONS = {
  EPISODES_REQUIRED: 3,
  NORMAL_DELAY_DAYS: 30,
  RATING_5_DELAY_DAYS: 90,
}
const ANONYMOUS_CONDITIONS = {
  EPISODES_REQUIRED: 5,
  USAGE_TIME_MINUTES: 10,
}

export const useFeedbackModal = () => {
  const { user } = useAuthContext()
  const router = useRouter()
  const [shouldShowModal, setShouldShowModal] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const sessionStartTimeRef = useRef<number>(Date.now())
  const usageTimeIntervalRef = useRef<NodeJS.Timeout>()

  // Get data from localStorage
  const getFeedbackData = useCallback((): FeedbackData => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        return JSON.parse(stored)
      }
    } catch (error) {
      console.error("Error reading feedback data from localStorage:", error)
    }
    return {
      episodesPlayed: [],
      anonymousEpisodesCount: 0,
      usageTimeMinutes: 0,
    }
  }, [])

  // Save data to localStorage
  const saveFeedbackData = useCallback((data: FeedbackData) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    } catch (error) {
      console.error("Error saving feedback data to localStorage:", error)
    }
  }, [])

  // Check if enough time has passed since last feedback or delay
  const isTimeConditionMet = useCallback(
    (data: FeedbackData): boolean => {
      const now = new Date()
      // Check if we're in a delay period
      if (data.delayUntil) {
        const delayUntil = new Date(data.delayUntil)
        if (now < delayUntil) {
          return false
        }
      }

      // Check normal delay period
      if (data.lastFeedbackDate) {
        const lastFeedback = new Date(data.lastFeedbackDate)
        const daysSinceLastFeedback = (now.getTime() - lastFeedback.getTime()) / (1000 * 60 * 60 * 24)

        if (user?.email) {
          return daysSinceLastFeedback >= LOGGED_USER_CONDITIONS.NORMAL_DELAY_DAYS
        } else {
          return false
        }
      }

      return true
    },
    [user?.email],
  )

  // Check if episode conditions are met
  const areEpisodeConditionsMet = useCallback(
    (data: FeedbackData): boolean => {
      if (user?.email) {
        // Logged users: count unique episodes
        return data.episodesPlayed.length >= LOGGED_USER_CONDITIONS.EPISODES_REQUIRED
      } else {
        // Anonymous users: count total episodes
        return data.anonymousEpisodesCount >= ANONYMOUS_CONDITIONS.EPISODES_REQUIRED
      }
    },
    [user?.email],
  )

  // Check if usage time condition is met (anonymous users only)
  const isUsageTimeConditionMet = useCallback(
    (data: FeedbackData): boolean => {
      if (user?.email) return false // Only for anonymous users
      return data.usageTimeMinutes >= ANONYMOUS_CONDITIONS.USAGE_TIME_MINUTES
    },
    [user?.email],
  )

  // Check if modal should be shown
  const checkShouldShowModal = useCallback(() => {
    // Only show modal on homepage
    if (router.pathname !== "/") {
      return false
    }

    const data = getFeedbackData()

    if (!isTimeConditionMet(data)) {
      return false
    }

    if (user?.email) {
      // Logged users: 3 different episodes
      return areEpisodeConditionsMet(data)
    } else {
      // Anonymous users: 5 episodes OR 10 minutes usage
      return areEpisodeConditionsMet(data) || isUsageTimeConditionMet(data)
    }
  }, [
    router.pathname,
    user?.email,
    getFeedbackData,
    isTimeConditionMet,
    areEpisodeConditionsMet,
    isUsageTimeConditionMet,
  ])

  // Track episode played
  const trackEpisodePlayed = useCallback(
    (episodeId: string) => {
      const data = getFeedbackData()

      if (user?.email) {
        // Logged users: track unique episodes
        if (!data.episodesPlayed.includes(episodeId)) {
          data.episodesPlayed.push(episodeId)
        }
      } else {
        // Anonymous users: increment count
        data.anonymousEpisodesCount += 1
      }

      saveFeedbackData(data)

      // Check if we should show modal after this episode
      if (checkShouldShowModal()) {
        setShouldShowModal(true)
      }
    },
    [user?.email, getFeedbackData, saveFeedbackData, checkShouldShowModal],
  )

  // Start tracking usage time (for anonymous users)
  const startUsageTimeTracking = useCallback(() => {
    if (user?.email) return // Only for anonymous users

    sessionStartTimeRef.current = Date.now()

    // Clear any existing interval
    if (usageTimeIntervalRef.current) {
      clearInterval(usageTimeIntervalRef.current)
    }

    // Update usage time every minute
    usageTimeIntervalRef.current = setInterval(() => {
      const data = getFeedbackData()
      const sessionMinutes = (Date.now() - sessionStartTimeRef.current) / (1000 * 60)
      data.usageTimeMinutes += 1 // Add 1 minute
      saveFeedbackData(data)

      // Check if we should show modal after this time update
      if (checkShouldShowModal()) {
        setShouldShowModal(true)
      }
    }, 60000) // Every minute
  }, [user?.email, getFeedbackData, saveFeedbackData, checkShouldShowModal])

  // Handle feedback submission
  const handleFeedbackSubmitted = useCallback(
    (rating: number, comment: string) => {
      const data = getFeedbackData()
      const now = new Date()

      data.lastFeedbackDate = now.toISOString()

      // If rating is 5 and no comment, delay for 90 days
      if (rating === 5 && !comment.trim()) {
        const delayDate = new Date(now.getTime() + LOGGED_USER_CONDITIONS.RATING_5_DELAY_DAYS * 24 * 60 * 60 * 1000)
        data.delayUntil = delayDate.toISOString()
      } else {
        // Remove any existing delay
        delete data.delayUntil
      }

      saveFeedbackData(data)
      setShouldShowModal(false)
      setIsModalOpen(false)
    },
    [getFeedbackData, saveFeedbackData],
  )

  // Handle modal dismissed without feedback
  const handleModalDismissed = useCallback(() => {
    const data = getFeedbackData()
    const now = new Date()

    // Track dismissal as a response with last feedback date
    data.lastFeedbackDate = now.toISOString()

    // Set delay for dismissed modal (shorter than normal delay)
    if (user?.email) {
      // For logged users: 7 days delay for dismissal
      const delayDate = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)
      data.delayUntil = delayDate.toISOString()
    } else {
      // For anonymous users: 3 days delay for dismissal
      const delayDate = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000)
      data.delayUntil = delayDate.toISOString()
    }

    saveFeedbackData(data)
    setShouldShowModal(false)
    setIsModalOpen(false)
  }, [getFeedbackData, saveFeedbackData, user?.email])

  // Show modal when conditions are met
  const showModal = useCallback(() => {
    if (shouldShowModal && !isModalOpen && router.pathname === "/") {
      setIsModalOpen(true)
    }
  }, [shouldShowModal, isModalOpen, router.pathname])

  // Initialize on component mount
  useEffect(() => {
    // Check initial conditions
    setShouldShowModal(checkShouldShowModal())

    // Start usage time tracking for anonymous users
    if (!user?.email) {
      startUsageTimeTracking()
    }

    return () => {
      // Cleanup interval on unmount
      if (usageTimeIntervalRef.current) {
        clearInterval(usageTimeIntervalRef.current)
      }
    }
  }, [user?.email, checkShouldShowModal, startUsageTimeTracking])

  // Update session start time when user changes
  useEffect(() => {
    if (!user?.email) {
      sessionStartTimeRef.current = Date.now()
      startUsageTimeTracking()
    } else {
      // Stop tracking for logged users
      if (usageTimeIntervalRef.current) {
        clearInterval(usageTimeIntervalRef.current)
      }
    }
  }, [user?.email, startUsageTimeTracking])

  // Re-evaluate modal conditions when route changes
  useEffect(() => {
    setShouldShowModal(checkShouldShowModal())
  }, [router.pathname, checkShouldShowModal])

  return {
    shouldShowModal,
    isModalOpen,
    showModal,
    trackEpisodePlayed,
    handleFeedbackSubmitted,
    handleModalDismissed,
    // Debug helpers
    debugData: getFeedbackData(),
  }
}

// Utility function to track episode completions from anywhere in the app
export const trackEpisode = (episodeId: string) => {
  try {
    const stored = localStorage.getItem("feedbackModalData")
    const data = stored
      ? JSON.parse(stored)
      : {
          episodesPlayed: [],
          anonymousEpisodesCount: 0,
          usageTimeMinutes: 0,
        }

    // Simple check for user type - if we have episodesPlayed array, assume logged user
    if (Array.isArray(data.episodesPlayed)) {
      if (!data.episodesPlayed.includes(episodeId)) {
        data.episodesPlayed.push(episodeId)
      }
    } else {
      data.anonymousEpisodesCount += 1
    }

    localStorage.setItem("feedbackModalData", JSON.stringify(data))
  } catch (error) {
    console.error("Error tracking episode completion:", error)
  }
}
